'use strict';

/* ── yuniq-kiosk.js ──────────────────────────────────────────────────────────
   Handles both kiosk pages:
   - YuniqKioskPhone  → <yuniq-kiosk-phone>  (/pages/kiosk)
   - YuniqKioskTablet → <yuniq-kiosk-tablet> (/pages/kiosk-tablet)
   ─────────────────────────────────────────────────────────────────────────── */

// ── Shared helpers ──────────────────────────────────────────────────────────

function genSessionCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

async function cloudinaryUpload(cloudName, preset, file, publicId) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', preset);
  fd.append('public_id', publicId);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) throw new Error(`Cloudinary upload failed: HTTP ${res.status}`);
  const data = await res.json();
  return data.secure_url;
}

async function cloudinaryRawUpload(cloudName, preset, blob, publicId) {
  // Wrap in a named File so Cloudinary uses our filename instead of the default "blob"
  const filename = publicId.split('/').pop() + '.pdf';
  const file = new File([blob], filename, { type: 'application/pdf' });
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', preset);
  fd.append('public_id', publicId);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) throw new Error(`Cloudinary raw upload failed: HTTP ${res.status}`);
  const data = await res.json();
  return data.secure_url;
}

// ── Signature canvas helper ─────────────────────────────────────────────────

class SignatureCanvas {
  constructor(canvas, hintEl) {
    this.canvas = canvas;
    this.hint = hintEl;
    this.ctx = canvas.getContext('2d');
    this.drawing = false;
    this.hasStrokes = false;
    this._resize();
    this._bind();
  }

  _resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.canvas.offsetWidth || 400;
    const h = this.canvas.offsetHeight || 180;
    this.canvas.width  = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.scale(dpr, dpr);
    this.ctx.strokeStyle = '#1a1a1a';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
  }

  _pos(e) {
    const rect = this.canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  }

  _bind() {
    const start = e => { e.preventDefault(); this.drawing = true; const p = this._pos(e); this.ctx.beginPath(); this.ctx.moveTo(p.x, p.y); };
    const move  = e => { e.preventDefault(); if (!this.drawing) return; const p = this._pos(e); this.ctx.lineTo(p.x, p.y); this.ctx.stroke(); if (!this.hasStrokes) { this.hasStrokes = true; if (this.hint) this.hint.classList.add('is-hidden'); } };
    const end   = () => { this.drawing = false; };
    this.canvas.addEventListener('mousedown', start);
    this.canvas.addEventListener('mousemove', move);
    this.canvas.addEventListener('mouseup', end);
    this.canvas.addEventListener('mouseleave', end);
    this.canvas.addEventListener('touchstart', start, { passive: false });
    this.canvas.addEventListener('touchmove', move, { passive: false });
    this.canvas.addEventListener('touchend', end);
  }

  clear() {
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    this.hasStrokes = false;
    if (this.hint) this.hint.classList.remove('is-hidden');
  }

  isEmpty() { return !this.hasStrokes; }

  toBlob(cb) { this.canvas.toBlob(cb, 'image/png'); }

  toDataURL() { return this.canvas.toDataURL('image/png'); }
}

// ── Consent document HTML template (kept for reference, no longer used) ─────
// (PDF is now generated directly via jsPDF text API in generateConsentPDF)

function buildConsentHTML(data) {
  const {
    session, customerName, cardDescription, designUrl, dateStr, sigDataUrl,
  } = data;

  const checkSVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="14" height="14" rx="3" fill="#b8892a"/><path d="M3 7L6 10L11 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: "Helvetica Neue", Arial, sans-serif;
    font-size: 11px;
    color: #1a1a1a;
    background: #fff;
    width: 595px;
    padding: 40px 48px;
    line-height: 1.5;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #b8892a;
    padding-bottom: 14px;
    margin-bottom: 20px;
  }
  .brand {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .brand span {
    display: block;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.18em;
    color: #b8892a;
    margin-top: 2px;
  }
  .meta {
    text-align: right;
    font-size: 10px;
    color: #555;
    line-height: 1.7;
  }
  .section {
    margin-bottom: 18px;
  }
  .section-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #b8892a;
    border-bottom: 1px solid #e8d9b5;
    padding-bottom: 5px;
    margin-bottom: 10px;
  }
  .info-row {
    display: flex;
    gap: 8px;
    margin-bottom: 5px;
  }
  .info-key {
    font-weight: 600;
    min-width: 130px;
    color: #555;
  }
  .consent-item {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 8px 10px;
    border: 1px solid #e8d9b5;
    border-radius: 5px;
    margin-bottom: 7px;
    background: #faf7f2;
  }
  .consent-item .check { flex-shrink: 0; margin-top: 1px; }
  .consent-item .text { font-size: 10.5px; line-height: 1.55; }
  .sig-box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 6px;
    background: #fff;
    display: inline-block;
    margin-top: 8px;
  }
  .sig-box img { display: block; max-width: 400px; height: 100px; object-fit: contain; }
  .sig-meta {
    font-size: 9.5px;
    color: #555;
    margin-top: 8px;
  }
  .design-link {
    font-size: 9.5px;
    word-break: break-all;
    color: #b8892a;
  }
  .footer {
    border-top: 1px solid #e0e0e0;
    padding-top: 12px;
    margin-top: 20px;
    font-size: 9px;
    color: #888;
    text-align: center;
  }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">YUNIQ<span>Metal Cards</span></div>
    </div>
    <div class="meta">
      Card Customisation Consent<br>
      Date: ${dateStr}<br>
      Session: ${session}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Customer Details</div>
    <div class="info-row"><span class="info-key">Customer Name:</span><span>${customerName || '—'}</span></div>
    <div class="info-row"><span class="info-key">Card Description:</span><span>${cardDescription || '—'}</span></div>
    ${designUrl ? `<div class="info-row"><span class="info-key">Design Upload:</span><span class="design-link">${designUrl}</span></div>` : ''}
  </div>

  <div class="section">
    <div class="section-title">Consent Statements</div>

    <div class="consent-item">
      <span class="check">${checkSVG}</span>
      <span class="text">I confirm that I am the lawful and authorized cardholder and that I voluntarily request this bespoke card-customization service.</span>
    </div>

    <div class="consent-item">
      <span class="check">${checkSVG}</span>
      <span class="text">I understand that issuer, payment-network, or legal restrictions may apply to customized cards or to the handling of card components, and that YUNIQ may require verification, additional documentation, or authorization before proceeding.</span>
    </div>

    <div class="consent-item">
      <span class="check">${checkSVG}</span>
      <span class="text">I understand that YUNIQ is not a bank or card issuer and does not guarantee issuer acceptance, payment-network acceptance, terminal compatibility, or continued functionality of the customized card.</span>
    </div>

    <div class="consent-item">
      <span class="check">${checkSVG}</span>
      <span class="text">I confirm that I have read and agree to the Terms and Conditions, Privacy Policy, and How it Works information, and that I have the legal right to use any submitted name, logo, artwork, or other design element.</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Signature</div>
    <div class="sig-box">
      <img src="${sigDataUrl}" alt="Customer signature">
    </div>
    <div class="sig-meta">Signed on: ${dateStr} (GST)</div>
  </div>

  <div class="footer">
    YUNIQ — In-Store Consent Record — Session ${session} — Generated ${dateStr}
  </div>
</body>
</html>`;
}

// ── PDF generation via jsPDF (no html2canvas, no iframe) ─────────────────────

async function generateConsentPDF(data) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const GOLD  = [184, 137, 42];
  const DARK  = [26, 26, 26];
  const GREY  = [110, 110, 110];
  const CREAM = [250, 247, 242];
  const GOLD_BORDER = [232, 217, 181];
  const M = 16;           // left/right margin mm
  const W = 210;          // A4 width mm
  const CW = W - M * 2;  // content width

  let y = 14;

  // ── Header ────────────────────────────────────────────────
  doc.setFillColor(...GOLD);
  doc.rect(M, y, CW, 0.6, 'F');
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...DARK);
  doc.text('YUNIQ', M, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...GOLD);
  doc.text('METAL CARDS', M, y + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GREY);
  doc.text('Card Customisation Consent', W - M, y, { align: 'right' });
  doc.text('Date: ' + data.dateStr, W - M, y + 5, { align: 'right' });
  doc.text('Session: ' + data.session, W - M, y + 10, { align: 'right' });

  y += 16;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(M, y, W - M, y);
  y += 7;

  // ── Customer details ──────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GOLD);
  doc.text('CUSTOMER DETAILS', M, y);
  y += 1.5;
  doc.setDrawColor(...GOLD_BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 5;

  const detailRow = (label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GREY);
    doc.text(label, M, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.text(value || '—', M + 36, y);
    y += 5.5;
  };
  detailRow('Customer Name:', data.customerName);
  detailRow('Email:', data.customerEmail);
  detailRow('Card Description:', data.cardDescription);
  if (data.designUrl) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GREY);
    doc.text('Design Upload:', M, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GOLD);
    const urlLines = doc.splitTextToSize(data.designUrl, CW - 36);
    doc.text(urlLines, M + 36, y);
    y += urlLines.length * 4 + 2;
  }
  y += 3;

  // ── Consent statements ────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GOLD);
  doc.text('CONSENT STATEMENTS', M, y);
  y += 1.5;
  doc.setDrawColor(...GOLD_BORDER);
  doc.line(M, y, W - M, y);
  y += 5;

  const consentItems = [
    'I confirm that I am the lawful and authorized cardholder and that I voluntarily request this bespoke card-customization service.',
    'I understand that issuer, payment-network, or legal restrictions may apply to customized cards or to the handling of card components, and that YUNIQ may require verification, additional documentation, or authorization before proceeding.',
    'I understand that YUNIQ is not a bank or card issuer and does not guarantee issuer acceptance, payment-network acceptance, terminal compatibility, or continued functionality of the customized card.',
    'I confirm that I have read and agree to the Terms and Conditions, Privacy Policy, and How it Works information, and that I have the legal right to use any submitted name, logo, artwork, or other design element.',
  ];

  consentItems.forEach(text => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(text, CW - 12);
    const boxH = lines.length * 4.5 + 5;

    // Box background
    doc.setFillColor(...CREAM);
    doc.setDrawColor(...GOLD_BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(M, y, CW, boxH, 1.5, 1.5, 'FD');

    // Gold checkmark box
    doc.setFillColor(...GOLD);
    doc.roundedRect(M + 2, y + 2, 5, 5, 0.8, 0.8, 'F');
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.9);
    const cx = M + 2, cy = y + 2;
    doc.line(cx + 1, cy + 3, cx + 2.3, cy + 4.5);
    doc.line(cx + 2.3, cy + 4.5, cx + 4.5, cy + 1.5);

    // Consent text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(lines, M + 10, y + 5);

    y += boxH + 2.5;
  });
  y += 3;

  // ── Signature ──────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GOLD);
  doc.text('SIGNATURE', M, y);
  y += 1.5;
  doc.setDrawColor(...GOLD_BORDER);
  doc.line(M, y, W - M, y);
  y += 5;

  if (data.sigDataUrl) {
    doc.setDrawColor(...GOLD_BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(M, y, CW, 28, 1.5, 1.5, 'S');
    doc.addImage(data.sigDataUrl, 'PNG', M + 2, y + 2, CW - 4, 24);
    y += 32;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GREY);
  doc.text('Signed on: ' + data.dateStr + ' (GST)', M, y);
  y += 12;

  // ── Footer ────────────────────────────────────────────────
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 4;
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text(
    'YUNIQ — In-Store Consent Record — Session ' + data.session + ' — Generated ' + data.dateStr,
    W / 2, y, { align: 'center' }
  );

  return doc.output('blob');
}

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOMER PHONE PAGE
// ═══════════════════════════════════════════════════════════════════════════

class YuniqKioskPhone extends HTMLElement {
  #cloudName         = '';
  #cloudPreset       = '';
  #session           = '';
  #finish            = '';
  #motif             = '';
  #text              = '';
  #frame             = '';
  #uploadFile        = null;
  #uploadedDesignUrl = '';
  #uploadDataUrl     = null;
  #uploadRenderMode  = 'ghost';
  #uploadDragX       = 0.5;
  #uploadDragY       = 0.5;
  #uploadDragScale   = 0.40;
  #sigPad            = null;
  #finishMap         = {};
  #motifMap          = {};
  #frameMap          = {};

  connectedCallback() {
    this.#cloudName   = this.dataset.cloudinaryCloud  || '';
    this.#cloudPreset = this.dataset.cloudinaryPreset || '';

    const params = new URLSearchParams(window.location.search);
    this.#session = params.get('session') || '';
    this.#finish  = params.get('finish')  || '';
    this.#motif   = params.get('motif')   || '';
    this.#text    = params.get('text')    || '';
    this.#frame   = params.get('frame')   || '';

    try { this.#finishMap = JSON.parse(this.querySelector('#ykp-finish-map')?.textContent || '{}'); } catch (_) {}
    try { this.#motifMap  = JSON.parse(this.querySelector('#ykp-motif-map')?.textContent  || '{}'); } catch (_) {}
    try { this.#frameMap  = JSON.parse(this.querySelector('#ykp-frame-map')?.textContent  || '{}'); } catch (_) {}

    this.#initScreen1();
    this.#initScreen2();
    this.#initScreen3();
    this.#initScreen4();
    this.#updatePreviews();
  }

  // ── Screen navigation ────────────────────────────────────────────────────────
  #showScreen(name) {
    this.querySelectorAll('.yk-screen').forEach(s => s.classList.remove('is-active'));
    this.querySelector(`#yk-screen-${name}`)?.classList.add('is-active');
    window.scrollTo(0, 0);
  }

  // ── Preview update (all three preview elements) ──────────────────────────────
  #updatePreviews() {
    const finish   = this.querySelector('#ykp-sel-finish')?.value || decodeURIComponent(this.#finish || '');
    const motifKey = this.querySelector('#ykp-sel-motif')?.value  || this.#motif || '';
    const frameKey = this.querySelector('#ykp-sel-frame')?.value  || this.#frame || 'none';
    const blankUrl = this.#finishMap[finish]  ?? null;
    const motifUrl = this.#motifMap[motifKey] ?? null;
    const frameUrl = this.#frameMap[frameKey] ?? null;
    const blankKey = finish.toLowerCase().replace(/\s*[—–-]\s*/g, '_').replace(/\s+/g, '_');
    const nameText = decodeURIComponent(this.#text || '');

    const p = { blankUrl, blankKey, motifUrl, nameText, frameUrl, frameKey,
                uploadDataUrl: this.#uploadDataUrl, uploadRenderMode: this.#uploadRenderMode,
                uploadOffsetX:    this.#uploadDataUrl ? this.#uploadDragX     : null,
                uploadOffsetY:    this.#uploadDataUrl ? this.#uploadDragY     : null,
                uploadScale:      this.#uploadDataUrl ? this.#uploadDragScale : null };

    ['#ykp-preview-1', '#ykp-preview-2', '#ykp-preview-mini'].forEach(id => {
      const el = this.querySelector(id);
      if (el) el.update(p);
    });

    // Screen 2 card labels + salutation
    const miniFinish = this.querySelector('#ykp-mini-finish');
    if (miniFinish) miniFinish.textContent = finish || '—';
    const miniMeta = this.querySelector('#ykp-mini-meta');
    if (miniMeta) {
      const parts = [
        motifKey ? this.#motifLabel(motifKey) : '',
        frameKey !== 'none' ? this.#frameLabel(frameKey) : '',
      ].filter(Boolean);
      miniMeta.textContent = parts.join(' · ');
    }
    const salutation = this.querySelector('#ykp-s2-salutation');
    if (salutation) {
      salutation.textContent = this.#uploadDataUrl
        ? 'Your card · With your design'
        : 'Your card · Add your design';
    }
  }

  // ── Screen 1: Review ─────────────────────────────────────────────────────────
  #initScreen1() {
    const finishSel = this.querySelector('#ykp-sel-finish');
    const motifSel  = this.querySelector('#ykp-sel-motif');
    const frameSel  = this.querySelector('#ykp-sel-frame');

    // Pre-select from URL params
    const finishName = decodeURIComponent(this.#finish || '');
    if (finishSel && finishName) finishSel.value = finishName;
    if (motifSel  && this.#motif) motifSel.value = this.#motif;
    if (frameSel  && this.#frame) frameSel.value = this.#frame;

    finishSel?.addEventListener('change', () => { this.#updatePreviews(); this.#buildSpecsTable(); });
    motifSel?.addEventListener('change',  () => { this.#updatePreviews(); this.#buildSpecsTable(); });
    frameSel?.addEventListener('change',  () => { this.#updatePreviews(); this.#buildSpecsTable(); });

    // Edit toggle
    const toggle = this.querySelector('#ykp-edit-toggle');
    const panel  = this.querySelector('#ykp-edit-panel');
    toggle?.addEventListener('click', () => panel?.classList.toggle('is-hidden'));

    this.#buildSpecsTable();

    this.querySelector('#ykp-btn-review-continue')?.addEventListener('click', () => {
      this.#showScreen('upload-consent');
    });
  }

  #buildSpecsTable() {
    const dl = this.querySelector('#ykp-specs');
    if (!dl) return;
    dl.innerHTML = '';

    const finish   = this.querySelector('#ykp-sel-finish')?.value || decodeURIComponent(this.#finish || '');
    const motifKey = this.querySelector('#ykp-sel-motif')?.value  || this.#motif || '';
    const frameKey = this.querySelector('#ykp-sel-frame')?.value  || this.#frame || 'none';
    const name     = decodeURIComponent(this.#text || '');

    const rows = [
      ['Body',       'Solid stainless steel'],
      ['Finish',     finish || '—'],
      ['Frame',      this.#frameLabel(frameKey)],
      ['Motif',      motifKey ? this.#motifLabel(motifKey) : '—'],
      ['Engraving',  name || '—'],
    ];
    rows.forEach(([k, v]) => {
      if (v === '—' && k !== 'Body' && k !== 'Finish') return;
      dl.insertAdjacentHTML('beforeend', `<dt>${k}</dt><dd>${v}</dd>`);
    });
  }

  #frameLabel(key) {
    const map = {
      none: '—', frame_4: 'Celtic Braid', frame_5: 'Belle Époque',
      frame_6: 'Baroque', frame_7: 'Classic', frame_8: 'Double Line',
      frame_9: 'Notched', frame_10: 'Greek Key', frame_11: 'Hearts', frame_12: 'Fine Hearts',
    };
    return map[key] || '—';
  }

  #motifLabel(handle) {
    const opt = this.querySelector(`#ykp-sel-motif option[value="${CSS.escape(handle)}"]`);
    return opt?.textContent?.trim() || handle;
  }

  // ── Screen 2: Upload + Consent ───────────────────────────────────────────────
  #initScreen2() {
    this.#setupDragVitrine(this.querySelector('#ykp-design-vitrine-s2'));

    const uploadInput   = this.querySelector('#ykp-upload-input');
    const uploadRemove  = this.querySelector('#ykp-upload-remove');

    if (uploadInput) {
      uploadInput.addEventListener('change', e => this.#handleFileSelect(e.target.files?.[0]));
    }

    // Drag-and-drop on the label container
    const uploadArea = this.querySelector('#ykp-upload-area');
    if (uploadArea) {
      uploadArea.addEventListener('dragover', e => { e.preventDefault(); });
      uploadArea.addEventListener('drop', e => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file && uploadInput) {
          const dt = new DataTransfer();
          dt.items.add(file);
          uploadInput.files = dt.files;
          this.#handleFileSelect(file);
        }
      });
    }

    uploadRemove?.addEventListener('click', e => { e.preventDefault(); this.#clearUpload(); });

    const slider = this.querySelector('#ykp-size-slider');
    slider?.addEventListener('input', () => {
      this.#uploadDragScale = parseFloat(slider.value) / 100;
      this.#updatePreviews();
    });

    // Consent checkboxes — visual toggle (no native <input type="checkbox">)
    this.querySelectorAll('[data-cb]').forEach(cb => {
      cb.closest('.yk-consent-row')?.addEventListener('click', () => {
        cb.classList.toggle('is-checked');
        cb.classList.remove('is-error');
        const errEl = this.querySelector('#ykp-consent-error');
        const allChecked = [...this.querySelectorAll('[data-cb]')].every(c => c.classList.contains('is-checked'));
        if (allChecked && errEl) errEl.classList.remove('is-visible');
      });
    });

    this.querySelector('#ykp-btn-consent-continue')?.addEventListener('click', () => {
      const cbs = [...this.querySelectorAll('[data-cb]')];
      const allChecked = cbs.every(c => c.classList.contains('is-checked'));
      if (!allChecked) {
        const errEl = this.querySelector('#ykp-consent-error');
        if (errEl) errEl.classList.add('is-visible');
        cbs.forEach(cb => { if (!cb.classList.contains('is-checked')) cb.classList.add('is-error'); });
        this.querySelector('[data-cb].is-error')
          ?.closest('.yk-consent-row')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (this.#uploadDataUrl) {
        this.#syncDesignReviewScreen();
        this.#showScreen('design-review');
      } else {
        this.#showScreen('signature');
      }
    });
  }

  #handleFileSelect(file) {
    if (!file) return;
    this.#uploadFile = file;
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    this.#uploadRenderMode = isSvg ? 'engrave' : 'ghost';

    const ext    = file.name.split('.').pop().toUpperCase() || '?';
    const typeEl = this.querySelector('#ykp-upload-type');
    const nameEl = this.querySelector('#ykp-upload-filename');
    if (typeEl) typeEl.textContent = ext;
    if (nameEl) nameEl.textContent = file.name;

    const reader = new FileReader();
    reader.onload = ev => {
      this.#uploadDataUrl = ev.target.result;
      const bigImg  = this.querySelector('#ykp-upload-img');
      const bigWrap = this.querySelector('#ykp-upload-big-wrap');
      if (bigImg)  bigImg.src = ev.target.result;
      if (bigWrap) bigWrap.classList.add('is-visible');
      const hint = this.querySelector('#ykp-drag-hint-s2');
      if (hint) hint.style.display = 'block';
      const sizeRow = this.querySelector('#ykp-size-row');
      if (sizeRow) sizeRow.style.display = 'flex';
      this.#updatePreviews();
    };
    reader.readAsDataURL(file);
  }

  #clearUpload() {
    this.#uploadFile        = null;
    this.#uploadedDesignUrl = '';
    this.#uploadDataUrl     = null;
    this.#uploadRenderMode  = 'ghost';
    this.#uploadDragX       = 0.5;
    this.#uploadDragY       = 0.5;
    this.#uploadDragScale   = 0.40;
    const input = this.querySelector('#ykp-upload-input');
    if (input) input.value = '';
    const bigWrap = this.querySelector('#ykp-upload-big-wrap');
    const bigImg  = this.querySelector('#ykp-upload-img');
    if (bigWrap) bigWrap.classList.remove('is-visible');
    if (bigImg)  bigImg.src = '';
    const hint = this.querySelector('#ykp-drag-hint-s2');
    if (hint) hint.style.display = 'none';
    const sizeRow   = this.querySelector('#ykp-size-row');
    const sizeSlider = this.querySelector('#ykp-size-slider');
    if (sizeRow)    sizeRow.style.display = 'none';
    if (sizeSlider) sizeSlider.value = 40;
    this.#updatePreviews();
  }

  // ── Screen 3: Custom Design Review ───────────────────────────────────────────
  #initScreen3() {
    this.querySelector('#ykp-btn-design-approve')?.addEventListener('click', () => {
      this.#showScreen('signature');
    });
    this.querySelector('#ykp-btn-replace-design')?.addEventListener('click', () => {
      this.#clearUpload();
      this.#showScreen('upload-consent');
    });
    this.#setupDragVitrine(this.querySelector('#ykp-design-vitrine'));
  }

  // ── Shared drag + pinch handler for any card vitrine ─────────────────────────
  #setupDragVitrine(vitrine) {
    if (!vitrine) return;

    // Track each active finger by pointer ID
    const pts = new Map(); // pointerId → {x, y}

    const getDist = () => {
      const arr = [...pts.values()];
      if (arr.length < 2) return null;
      return Math.hypot(arr[1].x - arr[0].x, arr[1].y - arr[0].y);
    };

    // Block iOS gesture recognizer before it fires pointercancel on the second touch
    vitrine.addEventListener('touchstart', e => {
      if (e.touches.length >= 2) e.preventDefault();
    }, { passive: false });

    vitrine.addEventListener('pointerdown', e => {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // NOTE: no setPointerCapture — it signals multi-touch intent to iOS, triggering pointercancel
      e.preventDefault();
    }, { passive: false });

    vitrine.addEventListener('pointermove', e => {
      if (!pts.has(e.pointerId)) return;

      const oldPos  = pts.get(e.pointerId);
      const oldDist = getDist();

      // Update this pointer's position
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const newDist = getDist();

      const card = vitrine.querySelector('yuniq-card-preview');
      const r    = card ? card.getBoundingClientRect() : vitrine.getBoundingClientRect();

      if (pts.size === 1) {
        // Single finger — drag
        const dx = e.clientX - oldPos.x;
        const dy = e.clientY - oldPos.y;
        this.#uploadDragX = Math.max(0, Math.min(1, this.#uploadDragX + dx / (r.width  || 300)));
        this.#uploadDragY = Math.max(0, Math.min(1, this.#uploadDragY + dy / (r.height || 190)));
      } else if (pts.size >= 2 && oldDist > 0 && newDist > 0) {
        // Two fingers — pinch to scale
        this.#uploadDragScale = Math.max(0.10, Math.min(0.85,
          this.#uploadDragScale * (newDist / oldDist)));
        // Sync slider thumb
        const slider = this.querySelector('#ykp-size-slider');
        if (slider) slider.value = Math.round(this.#uploadDragScale * 100);
      }

      this.#updatePreviews();
      e.preventDefault();
    }, { passive: false });

    const removePointer = e => pts.delete(e.pointerId);
    vitrine.addEventListener('pointerup',     removePointer);
    vitrine.addEventListener('pointercancel', removePointer);
  }

  #syncDesignReviewScreen() {
    if (!this.#uploadFile) return;
    const ext = this.#uploadFile.name.split('.').pop().toUpperCase() || '?';
    const typeEl = this.querySelector('#ykp-design-review-type');
    const nameEl = this.querySelector('#ykp-design-review-filename');
    if (typeEl) typeEl.textContent = ext;
    if (nameEl) nameEl.textContent = this.#uploadFile.name;
  }

  // ── Screen 4: Signature ──────────────────────────────────────────────────────
  #initScreen4() {
    const dateEl = this.querySelector('#ykp-date-display');
    if (dateEl) {
      dateEl.textContent = new Date().toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    }

    const sessEl = this.querySelector('#ykp-session-display');
    if (sessEl) sessEl.textContent = this.#session || 'Walk-in';

    const sigCanvas = this.querySelector('#ykp-sig-canvas');
    const sigHint   = this.querySelector('#ykp-sig-hint');
    if (sigCanvas) {
      setTimeout(() => { this.#sigPad = new SignatureCanvas(sigCanvas, sigHint); }, 200);
    }

    this.querySelector('#ykp-sig-clear')?.addEventListener('click', () => this.#sigPad?.clear());
    this.querySelector('#ykp-btn-submit')?.addEventListener('click', () => this.#handleSubmit());
  }

  // ── Submit ───────────────────────────────────────────────────────────────────
  async #handleSubmit() {
    const formError  = this.querySelector('#ykp-form-error');
    const emailErrEl = this.querySelector('#ykp-email-error');
    if (formError)  { formError.textContent  = ''; formError.classList.remove('is-visible'); }
    if (emailErrEl) { emailErrEl.textContent = ''; emailErrEl.classList.remove('is-visible'); }

    const emailInput = this.querySelector('#ykp-email');
    const emailVal   = emailInput?.value?.trim() || '';
    if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      if (emailErrEl) { emailErrEl.textContent = 'Please enter a valid email address.'; emailErrEl.classList.add('is-visible'); }
      emailInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (this.#sigPad?.isEmpty()) {
      if (formError) { formError.textContent = 'Please sign before submitting.'; formError.classList.add('is-visible'); }
      return;
    }

    const submitBtn = this.querySelector('#ykp-btn-submit');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }

    try {
      const now      = new Date();
      const dateSlug = now.toISOString().slice(0, 10);
      const dateStr  = now.toLocaleString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });

      // 1. Upload design file if provided
      if (this.#uploadFile && !this.#uploadedDesignUrl) {
        const publicId = `kiosk-designs/kiosk_${this.#session || 'walkin'}_design_${dateSlug}`;
        this.#uploadedDesignUrl = await cloudinaryUpload(
          this.#cloudName, this.#cloudPreset, this.#uploadFile, publicId
        );
      }

      const sigDataUrl    = this.#sigPad.toDataURL();
      const customerName  = decodeURIComponent(this.#text || '');
      const customerEmail = emailVal;
      const cardDesc      = [
        this.#finish ? decodeURIComponent(this.#finish) : '',
        customerName,
      ].filter(Boolean).join(', ');

      // 2. Generate consent PDF + upload to Cloudinary
      let consentPdfUrl = null;
      try {
        const blob = await generateConsentPDF({
          session:         this.#session || 'WALKIN',
          customerName,
          customerEmail,
          cardDescription: cardDesc,
          designUrl:       this.#uploadedDesignUrl,
          dateStr,
          sigDataUrl,
        });
        const pdfId = `kiosk-consent/kiosk_${this.#session || 'walkin'}_consent_${dateSlug}`;
        consentPdfUrl = await cloudinaryRawUpload(
          this.#cloudName, this.#cloudPreset, blob, pdfId
        );
      } catch (pdfErr) {
        console.warn('[YuniqKiosk] PDF upload skipped:', pdfErr);
      }

      // 3. Submit via Shopify contact form
      await this.#submitContactForm({
        customerName, customerEmail, cardDesc,
        designUrl:     this.#uploadedDesignUrl,
        consentPdfUrl,
        session:       this.#session || 'WALKIN',
        dateStr,
      });

      // 4. Success screen
      const specEl = this.querySelector('#ykp-success-spec');
      if (specEl) {
        const ref = this.#session
          ? `#YQ-${this.#session}`
          : `#YQ-${dateSlug.replace(/-/g, '')}`;
        specEl.textContent = `Order ${ref} · Saved`;
      }
      this.#showScreen('success');

    } catch (err) {
      console.error('[YuniqKiosk] Submit error:', err);
      if (formError) {
        formError.textContent = 'Something went wrong. Please try again or ask staff for help.';
        formError.classList.add('is-visible');
      }
      if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
    }
  }

  async #submitContactForm({ customerName, customerEmail, cardDesc, designUrl, consentPdfUrl, session, dateStr }) {
    const body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[name]',  customerName  || 'Kiosk Customer');
    body.set('contact[email]', customerEmail || 'kiosk@yuniq.ae');
    body.set('contact[body]',
      `[In-Store Consent] Session ${session} — ${dateStr}\n\n` +
      `Customer: ${customerName || '—'}\n` +
      `Email: ${customerEmail || '—'}\n` +
      `Card: ${cardDesc || '—'}\n\n` +
      (designUrl ? `Design upload: ${designUrl}\n\n` : '') +
      `CONSENT PDF: ${consentPdfUrl}`
    );
    const root = window?.Shopify?.routes?.root || '/';
    const res  = await fetch(`${root}contact`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
      body: body.toString(),
    });
    if (!res.ok) console.warn(`[YuniqKiosk] Contact form HTTP ${res.status} — submission may still have been recorded`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPLOYEE TABLET PAGE
// ═══════════════════════════════════════════════════════════════════════════

class YuniqKioskTablet extends HTMLElement {
  #cloudName      = '';
  #cloudPreset    = '';
  #shopUrl        = '';
  #motifs         = [];
  #session        = '';
  #selectedFinish = '';
  #selectedMotif  = '';
  #customText     = '';
  #qrInstance     = null;
  #preview          = null;
  #selectedBlankUrl = null;
  #selectedMotifUrl = null;
  #selectedFrameUrl = null;
  #selectedFrameKey = 'none';

  connectedCallback() {
    this.#cloudName   = this.dataset.cloudinaryCloud   || '';
    this.#cloudPreset = this.dataset.cloudinaryPreset  || '';
    this.#shopUrl     = this.dataset.shopUrl           || window.location.origin;
    try { this.#motifs = JSON.parse(this.dataset.motifs || '[]'); } catch {}

    this.#preview = this.querySelector('yuniq-card-preview');
    this.#renderMotifGrid();
    this.#bindControls();
  }

  #renderMotifGrid() {
    const grid = this.querySelector('#ykt-motifs-grid');
    if (!grid) return;
    grid.innerHTML = '';

    const noneCard = document.createElement('div');
    noneCard.className = 'ykt__design-card is-selected';
    noneCard.dataset.key = 'none';
    noneCard.dataset.url = '';
    noneCard.innerHTML = `<div style="width:100%;aspect-ratio:16/10;display:flex;align-items:center;justify-content:center;background:#f5f5f5;font-size:0.75rem;color:#888;">Custom / Upload</div><div class="ykt__design-name">Custom</div>`;
    grid.appendChild(noneCard);

    this.#motifs.forEach(m => {
      const card = document.createElement('div');
      card.className = 'ykt__design-card';
      card.dataset.key = m.key;
      card.dataset.url = m.url || m.thumb || '';
      card.innerHTML = `${m.thumb ? `<img src="${m.thumb}" alt="${m.label}" loading="lazy">` : `<div style="width:100%;aspect-ratio:16/10;background:#eee;"></div>`}<div class="ykt__design-name">${m.label}</div>`;
      grid.appendChild(card);
    });

    grid.addEventListener('click', e => {
      const card = e.target.closest('.ykt__design-card');
      if (!card) return;
      grid.querySelectorAll('.ykt__design-card').forEach(c => c.classList.remove('is-selected'));
      card.classList.add('is-selected');
      this.#selectedMotif    = card.dataset.key || 'none';
      this.#selectedMotifUrl = card.dataset.url || null;
      this.#updatePreview();
      this.#updateQR();
    });
  }

  #bindControls() {
    const createOrderBtn = this.querySelector('#ykt-create-order');
    if (createOrderBtn) {
      createOrderBtn.addEventListener('click', () => {
        this.#session = genSessionCode();
        const codeEl = this.querySelector('#ykt-session-code');
        if (codeEl) codeEl.textContent = this.#session;
        const panel = this.querySelector('#ykt-session-panel');
        panel?.classList.remove('is-hidden');
        panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.#updateQR();
      });
    }

    const finishSelect = this.querySelector('#ykt-finish');
    if (finishSelect) {
      finishSelect.addEventListener('change', () => {
        this.#selectedFinish = finishSelect.value;
        const swatch = this.querySelector('.ykt__finish-swatch.is-selected');
        this.#selectedBlankUrl = swatch?.dataset.blank || swatch?.dataset.image || null;
        this.#updatePreview();
        this.#updateQR();
      });
    }

    const textInput = this.querySelector('#ykt-text');
    if (textInput) {
      textInput.addEventListener('input', () => {
        this.#customText = textInput.value.trim();
        this.#updatePreview();
        this.#updateQR();
      });
    }

    const frameGrid = this.querySelector('#ykt-frames-grid');
    if (frameGrid) {
      frameGrid.addEventListener('click', e => {
        const btn = e.target.closest('.ykt__frame-btn');
        if (!btn) return;
        frameGrid.querySelectorAll('.ykt__frame-btn').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.#selectedFrameKey = btn.dataset.key || 'none';
        this.#selectedFrameUrl = btn.dataset.url || null;
        this.#updatePreview();
        this.#updateQR();
      });
    }
  }

  #updatePreview() {
    if (!this.#preview) return;
    this.#preview.update({
      blankUrl:  this.#selectedBlankUrl,
      blankKey:  this.#finishNameToKey(this.#selectedFinish),
      motifUrl:  this.#selectedMotifUrl,
      nameText:  this.#customText,
      frameUrl:  this.#selectedFrameUrl,
      frameKey:  this.#selectedFrameKey,
    });
  }

  #finishNameToKey(name) {
    return (name || '').toLowerCase()
      .replace(/\s*[—–]\s*/g, '_')
      .replace(/\s+/g, '_');
  }

  #buildKioskURL() {
    if (!this.#session) return '';
    const params = new URLSearchParams({ session: this.#session });
    if (this.#selectedFinish) params.set('finish', this.#selectedFinish);
    if (this.#selectedMotif && this.#selectedMotif !== 'none') params.set('motif', this.#selectedMotif);
    if (this.#customText) params.set('text', this.#customText);
    if (this.#selectedFrameKey && this.#selectedFrameKey !== 'none') params.set('frame', this.#selectedFrameKey);
    const previewId = this.dataset.previewThemeId;
    if (previewId) params.set('preview_theme_id', previewId);
    return `${this.#shopUrl}/pages/kiosk?${params.toString()}`;
  }

  #updateQR() {
    const url = this.#buildKioskURL();
    if (!url) return;

    const urlEl = this.querySelector('#ykt-qr-url');
    if (urlEl) urlEl.textContent = url;

    const qrEl = this.querySelector('#ykt-qr');
    if (!qrEl) return;

    // Clear previous QR
    qrEl.innerHTML = '';

    if (window.QRCode) {
      new window.QRCode(qrEl, {
        text: url,
        width: 200,
        height: 200,
        colorDark: '#1a1a1a',
        colorLight: '#ffffff',
        correctLevel: window.QRCode.CorrectLevel.M,
      });
    } else {
      qrEl.textContent = 'QR library not loaded.';
    }
  }
}

// ── Register custom elements ────────────────────────────────────────────────

customElements.define('yuniq-kiosk-phone',  YuniqKioskPhone);
customElements.define('yuniq-kiosk-tablet', YuniqKioskTablet);
